/**
 * useMonitorPolling — FEAT-MONITOR-001 (pattern ported from Hive's useClsPolling)
 * One shared timer batches all three monitor fetches (overview + throughput + utilization)
 * so widgets don't each spin up their own interval. On a failed poll we KEEP the last-good
 * payload and mark `stale` — the UI dims but never blanks. Throughput reloads immediately
 * when window/bucket/account change instead of waiting for the next tick.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWrapper } from "../../helpers";
import { DEFAULT_BUCKET_FOR } from "./helpers";

// Admin-monitor is its OWN service (e.g. https://adminmonitor.xnotify.com); it aggregates the
// per-box metrics agents. Falls back to the general API URL only for local dev.
const BASE = process.env.REACT_APP_MONITOR_API_URL || process.env.REACT_APP_API_URL;

export default function useMonitorPolling({ intervalMs = 10000, window = "6h", bucket = "5m", customerId = null }) {
  const [overview, setOverview] = useState(null);
  const [throughput, setThroughput] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);
  const [lastOkAt, setLastOkAt] = useState(null);
  const [busy, setBusy] = useState(false);

  // Refs so the stable `tick` callback always reads the latest selection.
  const cfg = useRef({ window, bucket, customerId });
  cfg.current = { window, bucket, customerId };

  const scope = useCallback((path) => {
    const cid = cfg.current.customerId;
    const sep = path.includes("?") ? "&" : "?";
    return cid ? `${path}${sep}customer_id=${cid}` : path;
  }, []);

  const get = useCallback((path) => fetchWrapper.get(`${BASE}${path}`), []);

  const tick = useCallback(async () => {
    setBusy(true);
    let anyOk = false;
    try {
      const { window: w, bucket: b } = cfg.current;
      const pipelineP = get("/admin/monitor/pipeline").then((r) => (r?.success !== false ? r.data : null)).catch(() => null);
      const overviewP = get(scope("/admin/monitor/overview"))
        .then((r) => (r?.success !== false ? r.data : null)).catch(() => null);
      const throughputP = get(scope(`/admin/monitor/throughput?window=${w}&bucket=${b}`))
        .then((r) => (r?.success !== false ? r.data : { _error: r })).catch((e) => ({ _error: e }));

      const [ov, th, pl] = await Promise.all([overviewP, throughputP, pipelineP]);
      if (ov) { setOverview(ov); anyOk = true; }
      if (th && !th._error) { setThroughput(th); anyOk = true; }
      if (pl) { setPipeline(pl); anyOk = true; }

      if (anyOk) { setStale(false); setLastOkAt(new Date().toISOString()); setError(null); }
      else { setStale(true); setError("Couldn't reach monitor API"); }
    } catch (e) {
      setStale(true); setError(e?.message || "Couldn't reach monitor API");
    } finally {
      setBusy(false);
    }
  }, [get, scope]);

  // Fire immediately, then on interval. intervalMs of 0 pauses (manual refresh still works).
  useEffect(() => {
    tick();
    if (!intervalMs) return undefined;
    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [tick, intervalMs]);

  // Reload the throughput series right away on window/bucket/account change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const path = scope(`/admin/monitor/throughput?window=${window}&bucket=${bucket}`);
        const r = await get(path);
        if (!cancelled && r?.success !== false) setThroughput(r.data);
      } catch (_) { /* keep old series */ }
    })();
    return () => { cancelled = true; };
  }, [window, bucket, customerId, get, scope]);

  return { overview, throughput, pipeline, error, stale, lastOkAt, busy, refresh: tick, DEFAULT_BUCKET_FOR };
}
